import { useQuery } from '@tanstack/react-query';

import type { DealBoardPerspective } from '@/api/deal-board-views';
import { _getIntroducerProfile } from '@/api/introducer-dashboard/queries';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import { useDealProviderPermissions } from '@/shared/hooks/use-deal-provider-permissions';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

export type DealBoardViewerPerspective = Extract<DealBoardPerspective, 'Provider' | 'Introducer'>;

/**
 * Resolves which read-only perspective (if any) the current session may view on
 * the Deals Pipeline (GP-3840, variant A — a NEW route, entirely separate from
 * the admin-only /deals-kanban, which stays gated by `Permission.KanbanItemView`
 * and is never touched here):
 *
 * - Provider: an 'entity' session whose entity is authorised as a provider AND
 *   currently `Verified` (`useDealProviderPermissions`'s `isProviderAuthorised`
 *   and `isEntityApproved`). Both are required, and the BFF requires both too —
 *   `isProvider` is granted once and outlives an approval change, so on its own
 *   it would keep a Blocked or re-verification-pending entity reading the board.
 * - Introducer: a personal user with a signed referral agreement, approved
 *   KYC, and an active Introducer membership. The Buyer pipeline is the fallback.
 *
 * A viewer who somehow qualifies as both gets Provider, matching the BFF's own
 * provider-wins-on-their-own-offers resolution (`DealBoardRowScopeResolver`).
 * Neither -> `perspective` is null and the caller must redirect away; nothing
 * here ever grants `Permission.KanbanItemView` to a non-Gamma role.
 */
export const useDealBoardViewerPerspective = (): {
  perspective: DealBoardViewerPerspective | null;
  isLoading: boolean;
} => {
  const { selectedUserRole } = useSelectedUserConfigSlice();
  const { user, isLoading: isProfileLoading, isError: isProfileError } = useGetUserProfile();
  const {
    isProviderAuthorised,
    isEntityApproved,
    isLoading: isProviderLoading,
  } = useDealProviderPermissions();
  const isEligiblePersonalUser =
    selectedUserRole === 'user' &&
    user?.kycStatus === 'APPROVED' &&
    user.isReferralAgreementSigned === true;
  const introducerProfileQuery = useQuery({
    ..._getIntroducerProfile(user?.id),
    enabled: isEligiblePersonalUser && !!user?.id && !isProfileError,
  });

  const isProvider =
    selectedUserRole === 'entity' && Boolean(isProviderAuthorised) && isEntityApproved;

  if (isProvider) return { perspective: 'Provider', isLoading: false };
  if (selectedUserRole === 'user') {
    const isMembershipLoading =
      isProfileLoading || (isEligiblePersonalUser && introducerProfileQuery.isLoading);
    if (isMembershipLoading) return { perspective: null, isLoading: true };
    if (
      isEligiblePersonalUser &&
      !isProfileError &&
      !introducerProfileQuery.isError &&
      introducerProfileQuery.data?.isActiveIntroducer === true
    ) {
      return { perspective: 'Introducer', isLoading: false };
    }
  }

  return {
    perspective: null,
    isLoading:
      selectedUserRole === 'entity'
        ? isProviderLoading
        : selectedUserRole === 'user' && isProfileLoading,
  };
};
